import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaEdit,
  FaEllipsisV,
  FaEnvelope,
  FaEye,
  FaFileAlt,
  FaFilter,
  FaGlobe,
  FaGlobeAmericas,
  FaPen,
  FaSearch,
  FaShieldAlt,
  FaUndo,
} from "react-icons/fa";
import CmsPreviewModal from "../../components/cms/CmsPreviewModal";
import {
  CMS_DASHBOARD_PAGE_TYPE_OPTIONS,
  CMS_DASHBOARD_QUICK_CARDS,
  CMS_PAGE_ROUTES,
  CMS_PAGE_TYPE_LABELS,
  TERMS_MODULE_INTRO,
  TERMS_MODULE_LABELS,
} from "../../constants/cms";
import { fetchCmsPages, fetchTermsList, cmsExtractMessage } from "../../services/cms.service";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/cms_management.scss";
import "../../assets/styles/cms_dashboard.scss";

const PAGE_EDITOR_BASE = "/super-admin/cms/pages";
const TERMS_EDITOR_BASE = "/super-admin/cms/terms";
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const LIST_FETCH_SIZE = 100;

const QUICK_ICONS = {
  terms: FaShieldAlt,
  building: FaBuilding,
  envelope: FaEnvelope,
  shield: FaShieldAlt,
  undo: FaUndo,
  globe: FaGlobe,
};

const slugByPageType = Object.fromEntries(
  CMS_DASHBOARD_QUICK_CARDS.filter((card) => card.pageType).map((card) => [
    card.pageType,
    card.slug,
  ]),
);

function getQuickCardTitle(card) {
  return card.title || CMS_PAGE_TYPE_LABELS[card.pageType] || "Page";
}

function getQuickCardLink(card) {
  return card.to || `${PAGE_EDITOR_BASE}/${card.slug}`;
}

function formatTableDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : value;
}

function normalizeCmsRow(item) {
  return {
    id: `cms-${item.id}`,
    sourceId: item.id,
    kind: "cms",
    pageKey: `cms:${item.pageType}`,
    pageLabel: CMS_PAGE_TYPE_LABELS[item.pageType] || item.pageType,
    title: item.title || CMS_PAGE_TYPE_LABELS[item.pageType] || "—",
    status: item.status,
    effectiveDate: item.effectiveDate,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
    createdBy: item.createdBy,
    pageType: item.pageType,
    content: item.content,
    description: item.description || "",
  };
}

function normalizeTermsRow(item) {
  const moduleLabel = TERMS_MODULE_LABELS[item.moduleName] || item.moduleName;
  return {
    id: `terms-${item.id}`,
    sourceId: item.id,
    kind: "terms",
    pageKey: `terms:${item.moduleName}`,
    pageLabel: "Terms & Conditions",
    title: moduleLabel,
    status: item.status,
    effectiveDate: item.effectiveDate,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
    createdBy: item.createdBy,
    moduleName: item.moduleName,
    content: item.content,
    description: TERMS_MODULE_INTRO[item.moduleName] || "",
  };
}

/** Keep one current record per CMS page / Terms module (prefer ACTIVE, then newest). */
function pickCurrentRows(rows) {
  const statusRank = { ACTIVE: 0, DRAFT: 1, INACTIVE: 2 };
  const byKey = new Map();

  rows.forEach((row) => {
    const existing = byKey.get(row.pageKey);
    if (!existing) {
      byKey.set(row.pageKey, row);
      return;
    }

    const existingRank = statusRank[existing.status] ?? 9;
    const nextRank = statusRank[row.status] ?? 9;
    if (nextRank < existingRank) {
      byKey.set(row.pageKey, row);
      return;
    }
    if (
      nextRank === existingRank &&
      new Date(row.updatedAt || 0).getTime() > new Date(existing.updatedAt || 0).getTime()
    ) {
      byKey.set(row.pageKey, row);
    }
  });

  return [...byKey.values()].sort(
    (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime(),
  );
}

function getEditUrl(item) {
  if (item.kind === "terms") {
    return `${TERMS_EDITOR_BASE}?module=${item.moduleName}`;
  }
  return `${PAGE_EDITOR_BASE}/${slugByPageType[item.pageType]}`;
}

function getPublicUrl(item) {
  if (item.kind === "terms") {
    return item.moduleName === "PLATFORM"
      ? "/terms-and-conditions"
      : `/terms-and-conditions?module=${item.moduleName}`;
  }
  return CMS_PAGE_ROUTES[item.pageType] || null;
}

function RowMenu({ item, onClose }) {
  const publicUrl = getPublicUrl(item);
  const editUrl = getEditUrl(item);

  return (
    <div className="cms-dashboard-row-menu__panel" role="menu">
      {publicUrl ? (
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="cms-dashboard-row-menu__item"
          role="menuitem"
          onClick={onClose}
        >
          <FaGlobeAmericas aria-hidden />
          View Public Page
        </a>
      ) : null}
      <Link to={editUrl} className="cms-dashboard-row-menu__item" role="menuitem" onClick={onClose}>
        <FaEdit aria-hidden />
        Open in Editor
      </Link>
    </div>
  );
}

export default function SuperAdminCmsDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState([]);
  const [filters, setFilters] = useState({
    page_type: "",
    search: "",
    page: 1,
    pageSize: 10,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    page_type: "",
    search: "",
    page: 1,
    pageSize: 10,
  });
  const [openRowMenuId, setOpenRowMenuId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    title: "",
    subtitle: "",
    contentTitle: "",
    contentIntro: "",
    content: "",
    effectiveDate: "",
    updatedAt: "",
    updatedBy: "",
    kind: "cms",
    publicUrl: "",
  });

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [cmsList, termsList] = await Promise.all([
        fetchCmsPages({ page: 1, page_size: LIST_FETCH_SIZE }),
        fetchTermsList({ page: 1, page_size: LIST_FETCH_SIZE }),
      ]);

      const merged = pickCurrentRows([
        ...(cmsList.items || []).map(normalizeCmsRow),
        ...(termsList.items || []).map(normalizeTermsRow),
      ]);

      setAllRows(merged);
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to load CMS dashboard"));
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".cms-dashboard-row-menu")) {
        setOpenRowMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const stats = useMemo(() => {
    const total = allRows.length;
    const active = allRows.filter((row) => row.status === "ACTIVE").length;
    return { total, active };
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let rows = allRows;

    if (appliedFilters.page_type === "TERMS") {
      rows = rows.filter((row) => row.kind === "terms");
    } else if (appliedFilters.page_type) {
      rows = rows.filter(
        (row) => row.kind === "cms" && row.pageType === appliedFilters.page_type,
      );
    }

    const search = appliedFilters.search?.trim().toLowerCase();
    if (search) {
      rows = rows.filter((row) => {
        const haystack = [row.pageLabel, row.title, row.moduleName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search);
      });
    }

    return rows;
  }, [allRows, appliedFilters]);

  const pagination = useMemo(() => {
    const total = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(total / appliedFilters.pageSize));
    return { total, totalPages };
  }, [filteredRows.length, appliedFilters.pageSize]);

  const items = useMemo(() => {
    const start = (appliedFilters.page - 1) * appliedFilters.pageSize;
    return filteredRows.slice(start, start + appliedFilters.pageSize);
  }, [filteredRows, appliedFilters.page, appliedFilters.pageSize]);

  const applyFilters = () => {
    setAppliedFilters((current) => ({
      ...filters,
      page: 1,
      pageSize: current.pageSize,
    }));
  };

  const openPreview = useCallback((item) => {
    if (!item.title?.trim() && !item.content?.trim()) {
      toast.info("Nothing to preview yet.");
      return;
    }

    setPreviewModal({
      title: item.pageLabel,
      subtitle: "",
      contentTitle: item.title || item.pageLabel,
      contentIntro: item.description || "",
      content: item.content || "",
      effectiveDate: item.effectiveDate,
      updatedAt: item.updatedAt,
      updatedBy: item.updatedBy || item.createdBy || "Super Admin",
      kind: item.kind,
      publicUrl: getPublicUrl(item) || "",
    });
    setPreviewOpen(true);
  }, []);

  const totalPages = pagination.totalPages || 1;
  const showingFrom =
    pagination.total === 0 ? 0 : (appliedFilters.page - 1) * appliedFilters.pageSize + 1;
  const showingTo = Math.min(appliedFilters.page * appliedFilters.pageSize, pagination.total);

  return (
    <div className="cms-dashboard-page">
      <div className="cms-dashboard-page__header">
        <div>
          <h1 className="cms-dashboard-page__title">CMS Management</h1>
        </div>
      </div>

      <div className="cms-dashboard-stats cms-dashboard-stats--compact">
        <article className="cms-dashboard-stat-card">
          <div className="cms-dashboard-stat-card__icon cms-dashboard-stat-card__icon--orange">
            <FaFileAlt aria-hidden />
          </div>
          <div>
            <p className="cms-dashboard-stat-card__label">Total Pages</p>
            <p className="cms-dashboard-stat-card__value">{stats.total}</p>
          </div>
        </article>

        <article className="cms-dashboard-stat-card">
          <div className="cms-dashboard-stat-card__icon cms-dashboard-stat-card__icon--green">
            <FaCheckCircle aria-hidden />
          </div>
          <div>
            <p className="cms-dashboard-stat-card__label">Active Pages</p>
            <p className="cms-dashboard-stat-card__value">{stats.active}</p>
          </div>
        </article>
      </div>

      <div className="cms-dashboard-quick">
        {CMS_DASHBOARD_QUICK_CARDS.map((card) => {
          const Icon = QUICK_ICONS[card.icon] || FaFileAlt;
          return (
            <Link
              key={card.pageType || card.kind}
              to={getQuickCardLink(card)}
              className="cms-dashboard-quick-card"
            >
              <span
                className={`cms-dashboard-quick-card__icon cms-dashboard-quick-card__icon--${card.icon}`}
                aria-hidden
              >
                <Icon />
              </span>
              <div className="cms-dashboard-quick-card__copy">
                <p className="cms-dashboard-quick-card__title">{getQuickCardTitle(card)}</p>
              </div>
              <FaPen className="cms-dashboard-quick-card__edit" aria-hidden />
            </Link>
          );
        })}
      </div>

      <div className="cms-dashboard-toolbar">
        <label className="cms-dashboard-toolbar__search">
          <FaSearch aria-hidden />
          <input
            type="search"
            placeholder="Search by page or title..."
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
        </label>
        <label className="cms-dashboard-toolbar__select">
          <select
            value={filters.page_type}
            onChange={(e) => setFilters((f) => ({ ...f, page_type: e.target.value }))}
          >
            <option value="">All Page Types</option>
            {CMS_DASHBOARD_PAGE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="cms-dashboard-btn cms-dashboard-btn--filter" onClick={applyFilters}>
          <FaFilter aria-hidden />
          Filter
        </button>
      </div>

      <section className="cms-dashboard-table-section">
        <h3 className="cms-dashboard-table-section__title">All Pages</h3>
        <div className="cms-dashboard-table-wrap">
          <table className="cms-dashboard-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Title</th>
                <th>Effective Date</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="cms-dashboard-table__empty">
                    Loading...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="cms-dashboard-table__empty">
                    No pages found.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.pageKey}>
                    <td>
                      <span className="cms-dashboard-table__page-cell">
                        <span className="cms-dashboard-table__page-icon" aria-hidden>
                          <FaFileAlt />
                        </span>
                        {item.pageLabel}
                      </span>
                    </td>
                    <td>{item.title || "—"}</td>
                    <td>
                      <span className="cms-dashboard-table__meta-cell">
                        <FaCalendarAlt aria-hidden />
                        {formatTableDate(item.effectiveDate)}
                      </span>
                    </td>
                    <td>
                      <span className="cms-dashboard-table__meta-cell">
                        <FaClock aria-hidden />
                        {formatTableDate(item.updatedAt)}
                      </span>
                    </td>
                    <td className="cms-dashboard-table__actions-cell">
                      <div className="cms-dashboard-table__actions">
                        <Link to={getEditUrl(item)} className="cms-dashboard-action-link">
                          <FaEdit aria-hidden />
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="cms-dashboard-action-link"
                          onClick={() => openPreview(item)}
                        >
                          <FaEye aria-hidden />
                          Preview
                        </button>
                        <div className="cms-dashboard-row-menu">
                          <button
                            type="button"
                            className="cms-dashboard-icon-btn"
                            aria-label="More actions"
                            aria-expanded={openRowMenuId === item.pageKey}
                            onClick={() =>
                              setOpenRowMenuId((current) =>
                                current === item.pageKey ? null : item.pageKey,
                              )
                            }
                          >
                            <FaEllipsisV aria-hidden />
                          </button>
                          {openRowMenuId === item.pageKey ? (
                            <RowMenu item={item} onClose={() => setOpenRowMenuId(null)} />
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="cms-dashboard-table-footer">
          <span>
            Showing {showingFrom} to {showingTo} of {pagination.total} entries
          </span>
          <label className="cms-dashboard-table-footer__page-size">
            Rows per page:
            <select
              value={appliedFilters.pageSize}
              onChange={(e) => {
                const pageSize = Number(e.target.value);
                setFilters((f) => ({ ...f, pageSize, page: 1 }));
                setAppliedFilters((f) => ({ ...f, pageSize, page: 1 }));
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <div className="cms-dashboard-table-footer__pager">
            <button
              type="button"
              className="cms-dashboard-icon-btn"
              disabled={appliedFilters.page <= 1}
              onClick={() => {
                setAppliedFilters((f) => ({ ...f, page: f.page - 1 }));
                setFilters((f) => ({ ...f, page: f.page - 1 }));
              }}
              aria-label="Previous page"
            >
              <FaChevronLeft aria-hidden />
            </button>
            <span className="cms-dashboard-table-footer__page-num">{appliedFilters.page}</span>
            <button
              type="button"
              className="cms-dashboard-icon-btn"
              disabled={appliedFilters.page >= totalPages}
              onClick={() => {
                setAppliedFilters((f) => ({ ...f, page: f.page + 1 }));
                setFilters((f) => ({ ...f, page: f.page + 1 }));
              }}
              aria-label="Next page"
            >
              <FaChevronRight aria-hidden />
            </button>
          </div>
        </div>
      </section>

      <CmsPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        variant={previewModal.kind === "terms" ? "terms" : "page"}
        title={previewModal.title}
        subtitle={previewModal.subtitle}
        contentTitle={previewModal.contentTitle}
        contentIntro={previewModal.contentIntro}
        content={previewModal.content}
        effectiveDate={previewModal.effectiveDate}
        updatedAt={previewModal.updatedAt}
        updatedBy={previewModal.updatedBy}
        publicUrl={previewModal.publicUrl || null}
      />
    </div>
  );
}
